import {
  computeChapterChangeStatuses,
  computeLessonChangeStatuses,
  reconcileCourse,
  type Course,
} from "@repo/app-types/entities/course";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type {
  ProjectWithInterviewCount,
  ProjectWithJobType,
} from "@repo/app-types/entities/project";
import type { LearningJourneyDraftSection } from "@repo/app-types/entities/trainingConfigurationTools";
import {
  applyTrainingOperations,
  type TrainingProposalOperation,
} from "@repo/app-types/entities/trainingProposal";
import isEqual from "lodash-es/isEqual.js";
import { useCallback, useMemo, useState } from "react";
import { useAllInterviewSectionDefinitionsForJobTypeQuery } from "../../../../api/entities/interviewSectionDefinition.js";
import { useUpdateJobTypeMutation } from "../../../../api/entities/jobType.js";
import { useAppDispatch } from "../../../../hooks/hook.js";
import {
  applyInterviewSectionDefinitionChanges,
  type ChangedInterviewSectionDefinition,
  type UnchangedInterviewSectionDefinition,
} from "../../../interviewSections/definitions/state.js";
import { useInterviewSectionDefinitionsStore } from "../../../interviewSections/definitions/store.js";
import { fetchProjectById } from "../../projectsSlice.js";
import { mapSectionClientIdsToServerIds } from "./courseHelpers.js";
import type { TreeItemKey } from "./courseTreeHelpers.js";

export function useLearningJourneyEditor(
  project: ProjectWithInterviewCount & ProjectWithJobType,
) {
  const jobTypeId = project.job_type.id;
  const dispatch = useAppDispatch();
  const [updateJobType, { isLoading: isSaving }] = useUpdateJobTypeMutation();
  const { data: fetchedSections, isLoading: isLoadingSections } =
    useAllInterviewSectionDefinitionsForJobTypeQuery({
      jobTypeId,
    });

  const {
    changedSections,
    onSectionCreate,
    onSectionUpdate,
    onSectionDelete,
    onSectionsSave,
    onSectionsReplace,
    onSectionsReset,
    hasMadeChanges: hasMadeChangesToSections,
  } = useInterviewSectionDefinitionsStore(jobTypeId);

  const sections = useMemo(
    () =>
      applyInterviewSectionDefinitionChanges(fetchedSections, changedSections),
    [fetchedSections, changedSections],
  );

  const sectionsById = useMemo(
    () =>
      sections
        ? new Map(sections.map((section) => [section.id, section] as const))
        : undefined,
    [sections],
  );

  const persistedCourse = useMemo(() => {
    if (!fetchedSections) {
      return project.job_type.settings.course ?? { chapters: [] };
    }

    return reconcileCourse(project.job_type.settings.course, fetchedSections);
  }, [project.job_type.settings.course, fetchedSections]);

  const [changedCourse, setChangedCourse] = useState<Course | undefined>(
    undefined,
  );
  const [changedPlaybookId, setChangedPlaybookId] = useState<
    PlaybookId | null | undefined
  >(undefined);

  const playbookId =
    changedPlaybookId === undefined
      ? project.job_type.settings.playbook_id
      : changedPlaybookId;

  const course = useMemo(() => {
    if (!sections) {
      return changedCourse ?? persistedCourse;
    }

    return reconcileCourse(changedCourse ?? persistedCourse, sections);
  }, [changedCourse, persistedCourse, sections]);

  const onCourseChange = useCallback(
    (nextCourse: Course) => {
      if (isEqual(nextCourse, persistedCourse)) {
        setChangedCourse(undefined);
        return;
      }

      setChangedCourse(nextCourse);
    },
    [persistedCourse],
  );

  const onPlaybookChange = useCallback(
    (nextPlaybookId: PlaybookId | null) => {
      if (nextPlaybookId === project.job_type.settings.playbook_id) {
        setChangedPlaybookId(undefined);
        return;
      }

      setChangedPlaybookId(nextPlaybookId);
    },
    [project.job_type.settings.playbook_id],
  );

  const editedLessonIds = useMemo(
    () =>
      changedSections
        .filter((section) => section.changeType === "updated")
        .map((section) => section.id),
    [changedSections],
  );

  const chapterChangeStatuses = useMemo(
    () =>
      computeChapterChangeStatuses(persistedCourse, course, {
        editedLessonIds,
      }),
    [persistedCourse, course, editedLessonIds],
  );

  const lessonChangeStatuses = useMemo(
    () =>
      computeLessonChangeStatuses(persistedCourse, course, {
        editedLessonIds,
      }),
    [persistedCourse, course, editedLessonIds],
  );

  const hasCourseChanges = !isEqual(course, persistedCourse);
  const hasPlaybookChanges =
    playbookId !== project.job_type.settings.playbook_id;
  const hasMadeChanges =
    hasCourseChanges || hasPlaybookChanges || hasMadeChangesToSections;

  /**
   * Seeds the draft from a proposal. A proposal stores operations, not a
   * course, so applying them to the saved training is what turns it into
   * something to show — the same replay the server runs to validate it.
   * Seeds in one pass: the per-operation handlers each read live state, so a
   * batch driven through them would compound stale reads. Throws
   * `TrainingOperationError` when the proposal no longer applies. Returns the
   * item to open so the proposed content is on screen.
   */
  const onProposalReplay = useCallback(
    (operations: readonly TrainingProposalOperation[]) => {
      const savedSections = fetchedSections ?? [];
      const replayed = applyTrainingOperations(
        {
          playbookId: project.job_type.settings.playbook_id,
          course: persistedCourse,
          sections: savedSections,
        },
        operations,
      );
      const sectionChanges = resolveSectionChanges(
        savedSections,
        replayed.sections,
      );
      onCourseChange(replayed.course);
      onSectionsReplace(sectionChanges);
      return resolveReplayedSelection(
        persistedCourse,
        replayed.course,
        sectionChanges,
      );
    },
    [
      fetchedSections,
      project.job_type.settings.playbook_id,
      persistedCourse,
      onCourseChange,
      onSectionsReplace,
    ],
  );

  const onDiscardChanges = useCallback(() => {
    setChangedCourse(undefined);
    setChangedPlaybookId(undefined);
    onSectionsReset();
  }, [onSectionsReset]);

  const onSaveChanges = useCallback(async () => {
    const savedSections = await onSectionsSave();
    await updateJobType({
      id: jobTypeId,
      settings: {
        ...project.job_type.settings,
        playbook_id: playbookId,
        course: mapSectionClientIdsToServerIds(course, savedSections),
      },
    }).unwrap();

    onSectionsReset();
    setChangedCourse(undefined);
    setChangedPlaybookId(undefined);
    await dispatch(fetchProjectById(project.id)).unwrap();
  }, [
    onSectionsSave,
    updateJobType,
    jobTypeId,
    project.job_type.settings,
    project.id,
    playbookId,
    course,
    onSectionsReset,
    dispatch,
  ]);

  return {
    course,
    sections,
    sectionsById,
    playbookId,
    changedSections,
    chapterChangeStatuses,
    lessonChangeStatuses,
    hasMadeChanges,
    isLoadingSections,
    isSaving,
    onCourseChange,
    onPlaybookChange,
    onSectionCreate,
    onSectionUpdate,
    onSectionDelete,
    onProposalReplay,
    onDiscardChanges,
    onSaveChanges,
  };
}

/**
 * The item to open after a replay: the first lesson the proposal added or
 * changed, in tree order, else the first chapter it touched.
 */
function resolveReplayedSelection(
  persistedCourse: Course,
  replayedCourse: Course,
  sectionChanges: readonly ChangedInterviewSectionDefinition[],
): TreeItemKey | undefined {
  const editedLessonIds = sectionChanges
    .filter((change) => change.changeType === "updated")
    .map((change) => change.id);

  const lessonStatuses = computeLessonChangeStatuses(
    persistedCourse,
    replayedCourse,
    { editedLessonIds },
  );
  for (const chapter of replayedCourse.chapters) {
    for (const lesson of chapter.lessons) {
      if (lessonStatuses.has(lesson.interviewSectionDefinitionId)) {
        return {
          type: "lesson",
          sectionId: lesson.interviewSectionDefinitionId,
        };
      }
    }
  }

  const chapterStatuses = computeChapterChangeStatuses(
    persistedCourse,
    replayedCourse,
    { editedLessonIds },
  );
  const changedChapter = replayedCourse.chapters.find((chapter) =>
    chapterStatuses.has(chapter.id),
  );
  return changedChapter
    ? { type: "chapter", id: changedChapter.id }
    : undefined;
}

/**
 * The section changes that turn the saved sections into the proposed ones.
 * A section the replay minted is created, one whose settings moved is updated,
 * and one the replay dropped is deleted.
 */
function resolveSectionChanges(
  savedSections: readonly UnchangedInterviewSectionDefinition[],
  proposedSections: readonly LearningJourneyDraftSection[],
): readonly ChangedInterviewSectionDefinition[] {
  const savedById = new Map(
    savedSections.map((section) => [section.id, section]),
  );
  const proposedIds = new Set(proposedSections.map((section) => section.id));
  const changes: ChangedInterviewSectionDefinition[] = [];

  for (const proposed of proposedSections) {
    const saved = savedById.get(proposed.id);
    if (!saved) {
      changes.push({ changeType: "created", ...proposed });
    } else if (!isEqual(saved.settings, proposed.settings)) {
      changes.push({ changeType: "updated", ...proposed });
    }
  }
  for (const saved of savedSections) {
    if (!proposedIds.has(saved.id)) {
      changes.push({ changeType: "deleted", id: saved.id });
    }
  }

  return changes;
}
